import React from 'react';
import SectionCTA from './SectionCTA';
import { HugeiconsIcon } from '@hugeicons/react';
import { GolfBallIcon, Award01Icon, GolfHoleIcon, Target03Icon, CourseIcon, StarIcon } from '@hugeicons/core-free-icons';

const Solution = () => {
  return (
    <section className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Golf Course Green Background with Liquid Glass Effect */}
      <div className="absolute inset-0" style={{ zIndex: 1 }}>
        <img
          src="https://res.cloudinary.com/dzcrxivpm/image/upload/v1758844807/openart-top-down-aerial-view-of-pristine-golf-course-green-with-freshly-cut-argyle-pattern-in-the-grass_h5pFjlH6_upscaled_usts94.webp"
          alt="Golf course green texture"
          width="1920"
          height="1080"
          className="parallax-bg opacity-99"
          data-speed="0.2"
          loading="lazy"
        />
      </div>
      {/* Liquid Glass Overlay */}
      <div className="absolute inset-0" style={{ zIndex: 2, background: 'rgba(2, 2, 2, 0.28)' }}>
        {/* Liquid glass effect overlay */}
        <div className="absolute inset-0 opacity-20" style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, transparent 40%, transparent 60%, rgba(255,255,255,0.05) 100%)' }}></div>
      </div>

      <div className="max-w-6xl mx-auto relative" style={{ zIndex: 2 }}>
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div className="flex flex-col h-full">
            <h2 className="font-playfair font-bold text-2xl sm:text-3xl md:text-4xl lg:text-5xl mb-8 leading-tight" style={{ color: '#facf21' }}>
              What If the Secret to Better Golf Has Been Walking Right Beside Tour Pros for Decades?
            </h2>

            <div className="p-8 rounded-3xl shadow-soft flex-1 flex flex-col justify-center" style={{ backgroundColor: '#153e20', color: 'white' }}>
              <p className="text-lg mb-6 leading-relaxed" style={{ color: '#ffffff' }}>
                Caddies don't just carry bags. They're strategic advisors. Course management experts. Mental game coaches.
              </p>
              <p className="text-lg mb-8 leading-relaxed" style={{ color: '#facf21' }}>
                They know something most golfers never learn: <strong style={{ color: 'white' }}>Golf isn't about hitting perfect shots. It's about making better decisions.</strong>
              </p>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-700 rounded-full flex items-center justify-center flex-shrink-0 shadow-lg">
                  <HugeiconsIcon icon={Award01Icon} size={20} />
                </div>
                <div>
                  <p className="font-semibold text-xl mb-2">Reality Check:</p>
                  <p className="leading-relaxed" style={{ color: '#facf21' }}>
                    Elite players hit it in the rough 40% of the time. They miss greens. They hit bad shots.
                    The difference is what they do next. How they think their way around the course.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center">
            <div className="p-10 rounded-3xl relative overflow-hidden" style={{
              background: 'rgba(84, 84, 84, 0.06)',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              boxShadow: 'inset 0 0 0 1px rgba(255, 255, 255, 0.5), inset 0 0 0 2px rgba(255, 255, 255, 0.2), 0 4px 24px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.08)'
            }}>
              {/* Liquid glass effect overlay */}
              <div className="absolute inset-0 opacity-30 pointer-events-none" style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.15) 0%, transparent 40%, transparent 60%, rgba(255,255,255,0.08) 100%)' }}></div>

              {/* Clean golf-themed photo with proper framing */}
              <div className="relative mx-auto mb-8 w-48 h-64 sm:w-56 sm:h-72 lg:w-64 lg:h-80 z-10">
                {/* Clean background frame */}
                <div className="absolute inset-0 bg-gradient-to-br from-green-600 to-green-800 rounded-2xl shadow-xl p-1">
                  <div className="w-full h-full bg-white rounded-xl"></div>
                </div>

                {/* Photo container - larger to show full head */}
                <div className="absolute inset-3 overflow-hidden rounded-xl shadow-lg">
                  <img
                    src="https://res.cloudinary.com/dzcrxivpm/image/upload/v1758771712/ryanheadshot_rwnmbn.webp"
                    alt="Ryan Tewksbury - Professional Caddie"
                    width="512"
                    height="683"
                    className="w-full h-full object-cover object-top scale-80"
                    loading="lazy"
                  />
                  {/* Subtle fade at bottom to soften harsh edge */}
                  <div className="absolute bottom-0 inset-x-0 h-12 bg-gradient-to-t from-white via-white/50 to-transparent pointer-events-none"></div>
                </div>

                {/* Golf ball icon in corner */}
                <div className="absolute top-2 right-2 w-8 h-8 bg-white rounded-full shadow-lg flex items-center justify-center">
                  <HugeiconsIcon icon={GolfBallIcon} size={20} />
                </div>
              </div>
              <h3 className="font-playfair font-bold text-3xl mb-3 relative z-10" style={{ color: '#F1D7C3' }}>Ryan Tewksbury</h3>
              <p className="text-white font-semibold text-lg mb-6 relative z-10">Caddie</p>
              <div className="text-left space-y-4 relative z-10" style={{ color: '#F1D7C3' }}>
                <div className="flex items-center">
                  <HugeiconsIcon icon={GolfHoleIcon} size={20} />
                  <span className="font-medium">10+ years caddying experience</span>
                </div>
                <div className="flex items-center">
                  <HugeiconsIcon icon={Target03Icon} size={20} />
                  <span className="font-medium">Worked with players at every skill level</span>
                </div>
                <div className="flex items-center">
                  <HugeiconsIcon icon={CourseIcon} size={20} />
                  <span className="font-medium">Thousands of rounds observed and analyzed</span>
                </div>
                <div className="flex items-center">
                  <HugeiconsIcon icon={StarIcon} size={20} />
                  <span className="font-medium">Battle-tested strategies that actually work</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Desktop CTA */}
        <SectionCTA className="mt-16" />
      </div>
    </section>
  );
};

export default Solution;